import { Box, Tab, Tabs, Typography } from '@mui/material'
import { useCallback } from 'react'

import { AceEditorWithoutForm } from '@/components/AceEditor/AceEditor'
import { FileImport } from '@/components/FileImport/FileImport'
import { useAppContext } from '@/hooks'

interface ImportIdleContentProps {
	fileError: string
	inputMode: 'file' | 'paste'
	onFileSelect: (files: File[]) => void
	onInputModeChange: (mode: 'file' | 'paste') => void
	onSqlTextChange: (value: string) => void
	selectedFile: File | null
	sqlText: string
	textError: string
}

const CONTENT_HEIGHT = '300px'
const ACE_EDITOR_HEIGHT = '280px'

export const ImportIdleContent = ({
	inputMode,
	onInputModeChange,
	onFileSelect,
	onSqlTextChange,
	selectedFile,
	sqlText,
	fileError,
	textError,
}: ImportIdleContentProps) => {
	const { t } = useAppContext()

	const handleTabChange = useCallback(
		(_event: React.SyntheticEvent, newValue: 'file' | 'paste') => {
			onInputModeChange(newValue)
		},
		[onInputModeChange],
	)

	return (
		<Box>
			<Tabs
				value={inputMode}
				onChange={handleTabChange}
				aria-label="import SQL input mode tabs"
				textColor="secondary"
				indicatorColor="secondary"
				className="mb-3"
			>
				<Tab label={t('IMPORT_SQL_TAB_UPLOAD')} value="file" />
				<Tab label={t('IMPORT_SQL_TAB_PASTE')} value="paste" />
			</Tabs>

			<Box sx={{ height: CONTENT_HEIGHT }}>
				{inputMode === 'file' && (
					<Box
						sx={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
						}}
					>
						<Box
							sx={{
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
							}}
						>
							<FileImport onFiles={onFileSelect} multiple={false} />

							{selectedFile && (
								<Typography variant="body2" className="mt-2">
									Selected: {selectedFile.name}
								</Typography>
							)}
						</Box>

						<Box sx={{ minHeight: '24px', mt: 1 }}>
							{fileError && (
								<Typography variant="body2" color="error">
									{fileError}
								</Typography>
							)}
						</Box>
					</Box>
				)}

				{inputMode === 'paste' && (
					<Box
						sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
					>
						<Box sx={{ flex: 1, minHeight: 0 }}>
							<AceEditorWithoutForm
								mode="sql"
								value={sqlText}
								onChange={(value) => {
									if (typeof value === 'string') {
										onSqlTextChange(value)
									}
								}}
								height={ACE_EDITOR_HEIGHT}
								width="100%"
								name="sql-import-editor"
							/>
						</Box>
						<Box sx={{ minHeight: '24px', mt: 1 }}>
							{textError && (
								<Typography variant="body2" color="error">
									{textError}
								</Typography>
							)}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	)
}
